'use client';

import _slicedToArray from "/codebuild/output/src1641/src/s3/01/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { useRef, useCallback, useEffect } from 'react';
import { useUncontrolled } from '@mantine/hooks';
import { getNextIndex, getPreviousIndex, getFirstIndex } from './get-index/get-index.mjs';
function useCombobox() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    defaultOpened = _ref.defaultOpened,
    opened = _ref.opened,
    onOpenedChange = _ref.onOpenedChange,
    onDropdownClose = _ref.onDropdownClose,
    onDropdownOpen = _ref.onDropdownOpen,
    _ref$loop = _ref.loop,
    loop = _ref$loop === void 0 ? true : _ref$loop,
    _ref$scrollBehavior = _ref.scrollBehavior,
    scrollBehavior = _ref$scrollBehavior === void 0 ? "instant" : _ref$scrollBehavior;
  var _useUncontrolled = useUncontrolled({
      value: opened,
      defaultValue: defaultOpened,
      finalValue: false,
      onChange: onOpenedChange
    }),
    _useUncontrolled2 = _slicedToArray(_useUncontrolled, 2),
    dropdownOpened = _useUncontrolled2[0],
    setDropdownOpened = _useUncontrolled2[1];
  var listId = useRef(null);
  var selectedOptionIndex = useRef(-1);
  var searchRef = useRef(null);
  var targetRef = useRef(null);
  var focusSearchTimeout = useRef(-1);
  var focusTargetTimeout = useRef(-1);
  var selectedIndexUpdateTimeout = useRef(-1);
  var openDropdown = useCallback(function () {
    var eventSource = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "unknown";
    if (!dropdownOpened) {
      setDropdownOpened(true);
      onDropdownOpen === null || onDropdownOpen === void 0 || onDropdownOpen(eventSource);
    }
  }, [setDropdownOpened, onDropdownOpen, dropdownOpened]);
  var closeDropdown = useCallback(function () {
    var eventSource = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "unknown";
    if (dropdownOpened) {
      setDropdownOpened(false);
      onDropdownClose === null || onDropdownClose === void 0 || onDropdownClose(eventSource);
    }
  }, [setDropdownOpened, onDropdownClose, dropdownOpened]);
  var toggleDropdown = useCallback(function () {
    var eventSource = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "unknown";
    if (dropdownOpened) {
      closeDropdown(eventSource);
    } else {
      openDropdown(eventSource);
    }
  }, [closeDropdown, openDropdown, dropdownOpened]);
  var clearSelectedItem = useCallback(function () {
    var selected = document.querySelector("#".concat(listId.current, " [data-combobox-selected]"));
    selected === null || selected === void 0 || selected.removeAttribute("data-combobox-selected");
    selected === null || selected === void 0 || selected.removeAttribute("aria-selected");
  }, []);
  var selectOption = useCallback(function (index) {
    var list = document.getElementById(listId.current);
    var items = list === null || list === void 0 ? void 0 : list.querySelectorAll("[data-combobox-option]");
    if (!items) {
      return null;
    }
    var nextIndex = index >= items.length ? 0 : index < 0 ? items.length - 1 : index;
    selectedOptionIndex.current = nextIndex;
    if (items !== null && items !== void 0 && items[nextIndex] && !items[nextIndex].hasAttribute("data-combobox-disabled")) {
      clearSelectedItem();
      items[nextIndex].setAttribute("data-combobox-selected", "true");
      items[nextIndex].setAttribute("aria-selected", "true");
      items[nextIndex].scrollIntoView({
        block: "nearest",
        behavior: scrollBehavior
      });
      return items[nextIndex].id;
    }
    return null;
  }, [scrollBehavior, clearSelectedItem]);
  var selectActiveOption = useCallback(function () {
    var activeOption = document.querySelector("#".concat(listId.current, " [data-combobox-active]"));
    if (activeOption) {
      var items = document.querySelectorAll("#".concat(listId.current, " [data-combobox-option]"));
      var index = Array.from(items).findIndex(function (option) {
        return option === activeOption;
      });
      return selectOption(index);
    }
    return selectOption(0);
  }, [selectOption]);
  var selectNextOption = useCallback(function () {
    return selectOption(getNextIndex(selectedOptionIndex.current, document.querySelectorAll("#".concat(listId.current, " [data-combobox-option]")), loop));
  }, [selectOption, loop]);
  var selectPreviousOption = useCallback(function () {
    return selectOption(getPreviousIndex(selectedOptionIndex.current, document.querySelectorAll("#".concat(listId.current, " [data-combobox-option]")), loop));
  }, [selectOption, loop]);
  var selectFirstOption = useCallback(function () {
    return selectOption(getFirstIndex(document.querySelectorAll("#".concat(listId.current, " [data-combobox-option]"))));
  }, [selectOption]);
  var updateSelectedOptionIndex = useCallback(function () {
    var target = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "selected";
    var options = arguments.length > 1 ? arguments[1] : undefined;
    selectedIndexUpdateTimeout.current = window.setTimeout(function () {
      var items = document.querySelectorAll("#".concat(listId.current, " [data-combobox-option]"));
      var index = Array.from(items).findIndex(function (option) {
        return option.hasAttribute("data-combobox-".concat(target));
      });
      selectedOptionIndex.current = index;
      if (options !== null && options !== void 0 && options.scrollIntoView) {
        var _items$index;
        (_items$index = items[index]) === null || _items$index === void 0 || _items$index.scrollIntoView({
          block: "nearest",
          behavior: scrollBehavior
        });
      }
    }, 0);
  }, []);
  var resetSelectedOption = useCallback(function () {
    selectedOptionIndex.current = -1;
    clearSelectedItem();
  }, [clearSelectedItem]);
  var clickSelectedOption = useCallback(function () {
    var items = document.querySelectorAll("#".concat(listId.current, " [data-combobox-option]"));
    var item = items === null || items === void 0 ? void 0 : items[selectedOptionIndex.current];
    item === null || item === void 0 || item.click();
  }, []);
  var setListId = useCallback(function (id) {
    listId.current = id;
  }, []);
  var focusSearchInput = useCallback(function () {
    focusSearchTimeout.current = window.setTimeout(function () {
      return searchRef.current.focus();
    }, 0);
  }, []);
  var focusTarget = useCallback(function () {
    focusTargetTimeout.current = window.setTimeout(function () {
      return targetRef.current.focus();
    }, 0);
  }, []);
  var getSelectedOptionIndex = useCallback(function () {
    return selectedOptionIndex.current;
  }, []);
  useEffect(function () {
    return function () {
      window.clearTimeout(focusSearchTimeout.current);
      window.clearTimeout(focusTargetTimeout.current);
      window.clearTimeout(selectedIndexUpdateTimeout.current);
    };
  }, []);
  return {
    dropdownOpened: dropdownOpened,
    openDropdown: openDropdown,
    closeDropdown: closeDropdown,
    toggleDropdown: toggleDropdown,
    selectedOptionIndex: selectedOptionIndex.current,
    getSelectedOptionIndex: getSelectedOptionIndex,
    selectOption: selectOption,
    selectFirstOption: selectFirstOption,
    selectActiveOption: selectActiveOption,
    selectNextOption: selectNextOption,
    selectPreviousOption: selectPreviousOption,
    resetSelectedOption: resetSelectedOption,
    updateSelectedOptionIndex: updateSelectedOptionIndex,
    listId: listId.current,
    setListId: setListId,
    clickSelectedOption: clickSelectedOption,
    searchRef: searchRef,
    focusSearchInput: focusSearchInput,
    targetRef: targetRef,
    focusTarget: focusTarget
  };
}
export { useCombobox };